import * as React from 'react';
import type { IDropdownOption } from '@fluentui/react';
import { Context } from '@micro-frontend-react/core/lib/Context';
import type { IEmployeeExperienceContext } from '@micro-frontend-react/employee-experience/lib/IEmployeeExperienceContext';

import { PBTApiEndpoints } from '../PBTConstants';

interface ILookupOptionResponse {
  value: string;
  code?: string | null;
  sortOrder?: number;
}

// Session-lifetime cache so each lookup category is fetched only once across all panels.
const lookupCache = new Map<string, IDropdownOption[]>();

/**
 * Loads dropdown options for a lookup category from the backend (dbo.LPMLookup) so the UI
 * stays in sync with the values the server validates against, eliminating hardcoded drift.
 * Falls back to the supplied static options while loading or if the request fails.
 */
export function usePbtLookupOptions(
  category: string,
  fallback: IDropdownOption[],
  mapCodeToKey = false
): { options: IDropdownOption[]; isLoading: boolean } {
  const { authClient } = React.useContext(Context as React.Context<IEmployeeExperienceContext>);
  // The mapped options depend on both the category and the mapping mode, so the cache must be
  // keyed by both. Otherwise a category first cached with mapCodeToKey=false would return value
  // keys to a later caller that requested mapCodeToKey=true (and vice versa).
  const cacheKey = React.useMemo(
    () => `${category}|${mapCodeToKey ? 'code' : 'value'}`,
    [category, mapCodeToKey]
  );
  const [options, setOptions] = React.useState<IDropdownOption[]>(
    () => lookupCache.get(cacheKey) ?? fallback
  );
  const [isLoading, setIsLoading] = React.useState<boolean>(() => !lookupCache.has(cacheKey));

  React.useEffect(() => {
    let cancelled = false;

    const cached = lookupCache.get(cacheKey);
    if (cached) {
      setOptions(cached);
      setIsLoading(false);
      return;
    }

    const load = async (): Promise<void> => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      try {
        const token = await authClient.acquireToken(__SCOPES__);
        const response = await fetch(PBTApiEndpoints.LookupOptions(category), {
          method: 'GET',
          headers: {
            Authorization: 'Bearer '.concat(token ?? ''),
            'Content-Type': 'application/json',
          },
          signal: controller.signal,
        });

        if (!response.ok) throw new Error('API returned '.concat(String(response.status)));

        const json = await response.json();
        const data = (json?.data ?? json?.Data ?? []) as ILookupOptionResponse[];
        const mapped: IDropdownOption[] = (Array.isArray(data) ? data : [])
          .filter((o) => !!o?.value)
          .map((o) => ({
            key: mapCodeToKey && o.code ? o.code : o.value,
            text: o.value,
          }));

        if (!cancelled && mapped.length > 0) {
          lookupCache.set(cacheKey, mapped);
          setOptions(mapped);
        }
      } catch {
        // Keep the static fallback options on any failure.
      } finally {
        clearTimeout(timeoutId);
        if (!cancelled) setIsLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [cacheKey, category, authClient, mapCodeToKey]);

  return { options, isLoading };
}
